import {useEffect} from "react";
import {useAuthStore} from "./auth-store";
import {useUserContextStore} from "./user-context-store";
import {usePermissionsStore} from "./permissions-store";

/**
 * Gestiona la expiración de la sesión de autenticación y limpia el estado local
 * cuando el usuario cierra sesión o cambia de usuario.
 * Esto es crítico en Module Federation para evitar que los remotes muestren
 * información del usuario anterior.
 */
export const GestionSesion = () => {
	const {isAuthenticated, expiracion, logout, isLoading, idUser} =
		useAuthStore();
	const clearAll = useUserContextStore((state) => state.clearAll);
	const clearPermissions = usePermissionsStore((state) => state.clearPermissions);

	// Gestionar expiración de sesión
	useEffect(() => {
		if (isLoading) {
			return;
		}

		let timerId: NodeJS.Timeout | undefined;

		if (isAuthenticated && expiracion) {
			const expiryDate = new Date(expiracion);
			const now = new Date();

			const expiresIn = expiryDate.getTime() - now.getTime();

			if (expiresIn <= 0) {
				logout();
			} else {
				timerId = setTimeout(() => {
					logout();
				}, expiresIn);
			}
		}

		return () => {
			if (timerId) {
				clearTimeout(timerId);
			}
		};

	}, [isAuthenticated, expiracion, logout, isLoading]);

	useEffect(() => {
		const handleLogout = () => {
			clearAll();
			clearPermissions();
			try {
				localStorage.removeItem('permissions-storage');
				localStorage.removeItem('user-context-storage');
			} catch (error) {
				console.error('Error al limpiar localStorage:', error);
			}

			// En Module Federation, es crítico recargar la página para limpiar
			// completamente el estado de los remotes que pueden tener datos en caché
			// del usuario anterior. Esto asegura que todos los módulos remotos
			// se reinicialicen con un estado limpio.
			setTimeout(() => {
				window.location.reload();
			}, 100);
		};

		const handleLogin = () => {
			// Nuevo usuario logueado
		};

		window.addEventListener('userLoggedOut', handleLogout);
		window.addEventListener('userLoggedIn', handleLogin);

		return () => {
			window.removeEventListener('userLoggedOut', handleLogout);
			window.removeEventListener('userLoggedIn', handleLogin);
		};
	}, [clearAll, clearPermissions]);

	// Detectar cambios de usuario (cuando idUser cambia)
	// Esto es importante cuando un usuario cierra sesión y otro inicia sesión
	useEffect(() => {
		if (!isAuthenticated || !idUser) {
			return;
		}

		// Cuando el usuario cambia (idUser cambia), forzar actualización
		// Los componentes que dependen de idUser se actualizarán automáticamente
		// pero podemos disparar un evento para notificar a los remotes
		const currentUserId = idUser;

		return () => {
			// Si el idUser cambia (cleanup del efecto anterior), significa que
			// el usuario cambió, así que limpiamos
			if (currentUserId !== idUser) {
				// Usuario cambió, limpiando estado
			}
		};
	}, [idUser, isAuthenticated]);

	return null;
};
